import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Layout } from '@/components';
import '@/assets/styles/views/create-account/create-account.styles.scss';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { VerificationCodeStep } from '@/screens/create-account/_components/verification-code-step';
import CreatePassword from '@/screens/create-account/_components/create-password';
import { StepId, FormData, StepProgress } from './account-create-form.types.ts';
import { STEPS } from './account-create-constants.ts';
import { schema } from '@/screens/create-account/_components/account-create-form/account-create-form.schema.ts';
import { AccountCreateForm } from '@/screens/create-account/_components/account-create-form';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider } from '@/hooks';
import { isVerified, jsonTryParse, responseOK } from '@/utility';
import { ExpertApplicantCreateRequest } from '@dopingtech/live_expert_applicant_bff_api_ts/dist/api';
import { UserType } from '@/types';
import { useLogin } from '@/hooks/use-login.tsx';
import { LocalStorageKeys } from '@/constants/local-storage-keys.ts';
import ExceptionManager from '@/managers/exception-manager.tsx';
import useDeviceInfo from '@/hooks/use-device-info.tsx';

const steps = Object.values(STEPS);

export const CreateAccountScreen: React.FC = () => {
	const { loginMutation } = useLogin();
	const { VerificationControllerApi, ExpertApplicantControllerApi, ContractControllerApiApplicant } = useApi();

	const sendVerificationCode = useMutationProvider((phoneNumber: string) => VerificationControllerApi.sendVerificationCode(phoneNumber));

	const createContractLog = useMutationProvider(contractId =>
		ContractControllerApiApplicant.createContractLog({
			contractId
		})
	);

	const verifyCode = useMutationProvider(({ phoneNumber, code }: { phoneNumber: string; code: string }) => VerificationControllerApi.verifyCode(phoneNumber, code));
	const createExpertApplicant = useMutationProvider((body: ExpertApplicantCreateRequest) => ExpertApplicantControllerApi.createExpertApplicant(body), {
		onError: error => {
			setDisableCreatePassword(false);
			ExceptionManager.errorHandler(error!);
		}
	});

	const [verificationCodeStatus, setVerificationCodeStatus] = useState<boolean | null>(null);
	const [currentStep, setCurrentStep] = useState<StepId>(STEPS.FORM.id);
	const [stepProgress, setStepProgress] = useState<StepProgress>({
		FORM: 0,
		VERIFY: 0,
		PASSWORD: 0
	});
	const [formState, setFormState] = useState<FormData>({
		userType: '',
		firstName: '',
		lastName: '',
		phone: '',
		acceptTerms: false,
		acceptMarketing: false,
		verificationCode: '',
		password: ''
	});
	const [disableCreatePassword, setDisableCreatePassword] = useState<boolean>(false);

	const navigation = useNavigate();
	const { deviceRequest } = useDeviceInfo();

	const {
		control,
		handleSubmit,
		formState: { errors },
		setValue
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues: formState
	});

	const currentIndex: number = useMemo(() => steps.findIndex(step => step.id === currentStep), [currentStep]);

	const handleFieldChange = (field: string, value: any) => {
		const scrollPosition = window.scrollY;

		setFormState((prev: any) => ({
			...prev,
			[field]: value
		}));

		const updatedForm = {
			...formState,
			[field]: value
		};

		const filledFields = STEPS.FORM.fields.filter(fieldName => {
			const fieldValue = updatedForm[fieldName as keyof typeof updatedForm];
			return fieldValue !== undefined && fieldValue !== '' && fieldValue !== false;
		});

		setStepProgress(prev => ({
			...prev,
			FORM: (filledFields.length / STEPS.FORM.fields.length) * 100
		}));

		requestAnimationFrame(() => {
			window.scrollTo({
				top: scrollPosition,
				behavior: 'auto'
			});
		});
	};

	const handleVerificationProgress = (code: string[]) => {
		const filledFields = code.filter(Boolean).length;
		const progress = (filledFields / 4) * 100;
		setStepProgress(prev => ({
			...prev,
			VERIFY: progress
		}));
	};

	const handlePasswordProgress = (passwordData: { password: string; confirmPassword: string }) => {
		const filledFields = Object.values(passwordData).filter(Boolean).length;
		setStepProgress(prev => ({
			...prev,
			PASSWORD: (filledFields / 2) * 100
		}));
	};

	const onSubmit = async (data: FormData) => {
		if (sendVerificationCode.isLoading) return;

		if (Object.keys(errors).length > 0) {
			setStepProgress(prev => ({ ...prev, FORM: 0 }));
			return;
		}

		const res = await sendVerificationCode.mutateAsync(data.phone);

		if (responseOK(res)) {
			setFormState(data);
			setCurrentStep(STEPS.VERIFY.id);
		}
	};

	const handleVerificationComplete = async ({ verificationCode }: { verificationCode: string }) => {
		if (verifyCode.isLoading) return;

		const res = await verifyCode.mutateAsync({
			phoneNumber: formState.phone,
			code: verificationCode
		});
		const verificationStatus = isVerified(res);

		setVerificationCodeStatus(verificationStatus);

		if (verificationStatus) {
			setFormState(prev => ({
				...prev,
				verificationCode
			}));
			setCurrentStep(STEPS.PASSWORD.id);
		}
	};

	const handlePasswordCreate = async (password: string) => {
		if (createExpertApplicant.isLoading) return;
		setDisableCreatePassword(true);

		const finalFormData: ExpertApplicantCreateRequest = {
			name: formState.firstName,
			surname: formState.lastName,
			specializationType: formState.userType as keyof UserType,
			phone: formState.phone,
			commercialConfirm: formState.acceptMarketing as boolean,
			verificationCode: formState.verificationCode as string,
			password
		};

		const res = await createExpertApplicant.mutateAsync(finalFormData);

		if (responseOK(res)) {
			loginMutation(
				{
					phoneNumber: formState.phone.replace(/\D/g, ''),
					password: password,
					deviceRequest: deviceRequest
				},
				() => {
					const registerContracts = localStorage.getItem(LocalStorageKeys.REGISTER_CONTRACTS);
					const _registerContracts = jsonTryParse(registerContracts);

					if (_registerContracts?.length) {
						_registerContracts.map(contract => {
							createContractLog.mutateAsync(contract.id);
						});

						localStorage.removeItem(LocalStorageKeys.REGISTER_CONTRACTS);
					}
					navigation(NavigationPath.applicationOnboarding, {
						state: {
							data: finalFormData
						}
					});
				}
			);
		} else {
			setDisableCreatePassword(false);
		}
	};

	const renderStepIndicator = () => {
		return (
			<div className="step-indicator">
				<div className="step-progress">
					{steps.map((step, index) => (
						<div
							key={step.id}
							className="step-segment">
							<div className="step-bar">
								<div
									className="step-bar__progress"
									style={{
										width: index <= currentIndex ? `${stepProgress[step.id]}%` : '0%'
									}}
								/>
							</div>
							<div
								className={`step-dot ${
									index === 0 ? 'step-dot--active' : index === currentIndex ? 'step-dot--active' : index < currentIndex && stepProgress[step.id] === 100 ? 'step-dot--completed' : ''
								}`}
							/>
						</div>
					))}
				</div>
			</div>
		);
	};

	const stepComponents = {
		[STEPS.FORM.id]: (
			<AccountCreateForm
				formState={formState}
				handleFieldChange={handleFieldChange}
				form={{
					control,
					handleSubmit,
					formState: { errors },
					setValue
				}}
				disabled={sendVerificationCode.isLoading}
				onSubmit={onSubmit}
			/>
		),
		[STEPS.VERIFY.id]: (
			<VerificationCodeStep
				formData={formState}
				onNext={handleVerificationComplete}
				onProgress={handleVerificationProgress}
				verificationCodeStatusIsOk={verificationCodeStatus}
				disabled={verifyCode.isLoading}
			/>
		),
		[STEPS.PASSWORD.id]: (
			<CreatePassword
				onNext={handlePasswordCreate}
				onProgress={handlePasswordProgress}
				disabled={createExpertApplicant.isLoading || disableCreatePassword}
			/>
		)
	};

	return (
		<Layout>
			<div className="create-account-container">
				{renderStepIndicator()}
				{stepComponents[currentStep]}
			</div>
		</Layout>
	);
};
